import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MdDomain,
  MdArrowBack,
  MdBarChart,
  MdPieChart,
  MdPeople,
  MdRefresh,
  MdSearch,
  MdSupervisorAccount,
  MdEngineering,
  MdTrendingUp,
  MdStar,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import { getSectors } from "api/sectors";
import { getSectorReport } from "api/kpiReports";
import Chart from "react-apexcharts";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

/* ── helpers ── */
function pctColor(pct) {
  if (pct >= 80) return "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20";
  if (pct >= 60) return "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20";
  return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20";
}

/* ── helper: map weighted percentage to performance label ── */
function performanceLabel(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "—";
  const n = Number(pct);
  if (n >= 90) return "Excellent";
  if (n >= 75) return "Very Good";
  if (n >= 60) return "Good";
  return "Bad";
}

function pctBarColor(pct) {
  if (pct >= 80) return "#10b981";
  if (pct >= 60) return "#f59e0b";
  return "#ef4444";
}

function roleBadgeStyle(role) {
  if (role === "supervisor") return "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300";
  if (role === "operator") return "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300";
  return "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300";
}

function RoleIcon({ role }) {
  if (role === "supervisor") return <MdSupervisorAccount className="text-sm" />;
  return <MdEngineering className="text-sm" />;
}

/* ── Score Ring ── */
function ScoreRing({ percentage, size = 140 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = pctBarColor(percentage);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={8} className="text-slate-100 dark:text-slate-800" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={8} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-slate-900 dark:text-white">{percentage.toFixed(1)}%</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">sector avg</span>
      </div>
    </div>
  );
}

/* ── Worker row card ── */
function WorkerCard({ worker, rank }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
      {/* Rank */}
      <span
        className="flex items-center justify-center w-9 h-9 rounded-xl text-white text-sm font-black shrink-0"
        style={{ backgroundColor: rank <= 3 ? SECONDARY : "#94a3b8" }}
      >
        {rank}
      </span>

      {/* Avatar initial */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-black shrink-0"
        style={{ backgroundColor: PRIMARY }}
      >
        {worker.evaluatee_name?.charAt(0)?.toUpperCase() || "W"}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{worker.evaluatee_name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${roleBadgeStyle(worker.evaluatee_role)}`}>
            <RoleIcon role={worker.evaluatee_role} />
            {worker.evaluatee_role}
          </span>
          <span className="text-xs text-slate-400">{worker.kpi_count} KPI{worker.kpi_count !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Score + progress bar */}
      <div className="shrink-0 w-32 sm:w-40">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-black ${pctColor(worker.weighted_percentage)}`}>
            {performanceLabel(worker.weighted_percentage)}
          </span>
          <span className="text-[10px] text-slate-400 font-medium">wt: {worker.total_weight}</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, worker.weighted_percentage)}%`, backgroundColor: pctBarColor(worker.weighted_percentage) }}
          />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════════ */
export default function SectorReport({ onBack }) {
  const [sectorsList, setSectorsList] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [selectedSectorId, setSelectedSectorId] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  /* Fetch sectors for the dropdown */
  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    try {
      const result = await getSectors();
      setSectorsList(result.data || []);
    } catch {
      setSectorsList([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  /* Fetch sector report when sector is selected */
  const fetchReport = useCallback(async (sectorId, fromDateParam, toDateParam) => {
    if (!sectorId) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await getSectorReport(sectorId, fromDateParam, toDateParam);
      setReport(result.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSectorId) fetchReport(selectedSectorId, fromDate, toDate);
  }, [selectedSectorId, fetchReport, fromDate, toDate]);

  /* Filtered sectors for dropdown */
  const filteredSectors = useMemo(() => {
    if (!search.trim()) return sectorsList;
    const q = search.trim().toLowerCase();
    return sectorsList.filter((s) => s.name.toLowerCase().includes(q));
  }, [sectorsList, search]);

  /* Derived data */
  const workers = report?.workers || [];

  const sortedWorkers = useMemo(
    () => [...workers].sort((a, b) => b.weighted_percentage - a.weighted_percentage),
    [workers]
  );

  const totalPages = Math.max(1, Math.ceil(sortedWorkers.length / PAGE_SIZE));
  const paginatedWorkers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedWorkers.slice(start, start + PAGE_SIZE);
  }, [sortedWorkers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSectorId, report?.workers?.length]);

  const roleDistribution = useMemo(() => {
    const counts = {};
    workers.forEach((w) => {
      const role = w.evaluatee_role || "other";
      counts[role] = (counts[role] || 0) + 1;
    });
    return { labels: Object.keys(counts), series: Object.values(counts) };
  }, [workers]);

  const performanceDistribution = useMemo(() => {
    const buckets = { "Excellent (80-100%)": 0, "Good (60-79%)": 0, "Needs Improvement (<60%)": 0 };
    workers.forEach((w) => {
      if (w.weighted_percentage >= 80) buckets["Excellent (80-100%)"] += 1;
      else if (w.weighted_percentage >= 60) buckets["Good (60-79%)"] += 1;
      else buckets["Needs Improvement (<60%)"] += 1;
    });
    return { labels: Object.keys(buckets), series: Object.values(buckets) };
  }, [workers]);

  const topPerformer = sortedWorkers[0];
  const lowestPerformer = sortedWorkers[sortedWorkers.length - 1];

  const sector = report?.sector;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <MdArrowBack className="text-lg" />
            Back
          </button>
          <div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Sector Report
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
              Select a sector to view its performance analytics
            </p>
          </div>
        </div>
        {selectedSectorId && report && (
          <button
            type="button"
            onClick={() => fetchReport(selectedSectorId)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <MdRefresh className={`text-lg ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Sector selector */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-5">
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-3">
          Select a sector
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sectors..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none"
            />
          </div>
          <select
            value={selectedSectorId}
            onChange={(e) => setSelectedSectorId(e.target.value)}
            disabled={sectorsLoading}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 min-w-[240px] focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none disabled:opacity-60"
          >
            <option value="">{sectorsLoading ? "Loading sectors..." : "Choose sector..."}</option>
            {filteredSectors.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <div className="flex items-center gap-2 mt-2 sm:mt-0">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2"
              aria-label="From date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2"
              aria-label="To date"
            />
            <button
              type="button"
              onClick={() => fetchReport(selectedSectorId, fromDate, toDate)}
              disabled={!selectedSectorId}
              className="px-4 py-2 rounded-xl bg-[#0e76a8] text-white font-bold text-sm disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-16 text-center">
          <div className="h-10 w-10 mx-auto animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-[#0e76a8] mb-4" />
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Loading sector report...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-5 flex items-center justify-between gap-4">
          <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
          <button
            type="button"
            onClick={() => fetchReport(selectedSectorId)}
            className="px-4 py-2 rounded-xl bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm font-bold hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!selectedSectorId && !loading && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 p-16 text-center">
          <MdDomain className="mx-auto text-5xl text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            Select a sector above to view its evaluation report
          </p>
        </div>
      )}

      {/* No workers evaluated / no data */}
      {selectedSectorId && !loading && (!report || workers.length === 0) && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 p-16 text-center">
          <MdPeople className="mx-auto text-5xl text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            No evaluation data for this sector
          </p>
        </div>
      )}

      {/* Report content */}
      {report && sector && !loading && workers.length > 0 && (
        <>
          {/* Sector header + overall score */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sector info */}
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl shrink-0"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <MdDomain className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">{sector.sector_name}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Sector performance overview</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Workers evaluated</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">{report.total_workers_evaluated}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Supervisors</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">
                    {workers.filter((w) => w.evaluatee_role === "supervisor").length}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Operators</p>
                  <p className="text-xl font-black text-slate-900 dark:text-white">
                    {workers.filter((w) => w.evaluatee_role === "operator").length}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Sector avg</p>
                  <p className={`text-xl font-black ${report.sector_average_percentage >= 80 ? "text-emerald-600" : report.sector_average_percentage >= 60 ? "text-amber-600" : "text-red-600"}`}>
                    {report.sector_average_percentage.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Overall score ring */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6 flex flex-col items-center justify-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Sector average</p>
              <ScoreRing percentage={report.sector_average_percentage} />
              <p className="text-sm font-bold text-slate-600 dark:text-slate-400 mt-3">
                {performanceLabel(report.sector_average_percentage)}
              </p>
            </div>
          </div>

          {/* Highlight cards: Top & Lowest performer */}
          {workers.length > 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Top performer */}
              {topPerformer && (
                <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MdArrowUpward className="text-lg text-emerald-600 dark:text-emerald-400" />
                    <span className="text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Top performer</span>
                    <MdStar className="text-lg text-amber-400 ml-auto" />
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-white">{topPerformer.evaluatee_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${roleBadgeStyle(topPerformer.evaluatee_role)}`}>
                      <RoleIcon role={topPerformer.evaluatee_role} />
                      {topPerformer.evaluatee_role}
                    </span>
                    <span className={`text-sm font-black ml-auto ${pctColor(topPerformer.weighted_percentage)}`}>
                      {performanceLabel(topPerformer.weighted_percentage)}
                    </span>
                  </div>
                </div>
              )}

              {/* Lowest performer */}
              {lowestPerformer && topPerformer?.evaluatee_id !== lowestPerformer?.evaluatee_id && (
                <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-slate-900 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MdArrowDownward className="text-lg text-red-500 dark:text-red-400" />
                    <span className="text-xs font-black uppercase tracking-wider text-red-500 dark:text-red-400">Needs attention</span>
                  </div>
                  <p className="text-lg font-black text-slate-900 dark:text-white">{lowestPerformer.evaluatee_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${roleBadgeStyle(lowestPerformer.evaluatee_role)}`}>
                      <RoleIcon role={lowestPerformer.evaluatee_role} />
                      {lowestPerformer.evaluatee_role}
                    </span>
                    <span className={`text-sm font-black ml-auto ${pctColor(lowestPerformer.weighted_percentage)}`}>
                      {performanceLabel(lowestPerformer.weighted_percentage)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Worker performance bar chart */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <MdBarChart className="text-xl" style={{ color: PRIMARY }} />
                <h3 className="font-black text-slate-900 dark:text-white">Worker Performance (%)</h3>
              </div>
              <div className="h-72">
                <Chart
                  type="bar"
                  width="100%"
                  height="100%"
                  series={[{
                    name: "Weighted %",
                    data: sortedWorkers.map((w) => Math.round(w.weighted_percentage * 10) / 10),
                  }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 8, columnWidth: "55%", distributed: true } },
                    xaxis: {
                      categories: sortedWorkers.map((w) => w.evaluatee_name.length > 14 ? w.evaluatee_name.slice(0, 12) + "..." : w.evaluatee_name),
                      labels: { style: { colors: "#64748b", fontSize: "11px" }, rotate: -30, trim: true },
                    },
                    yaxis: { min: 0, max: 100, labels: { style: { colors: "#64748b" } } },
                    colors: sortedWorkers.map((w) => pctBarColor(w.weighted_percentage)),
                    dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { fontSize: "11px" } },
                    grid: { borderColor: "rgba(148,163,184,0.15)", strokeDashArray: 4 },
                    tooltip: { theme: "light", y: { formatter: (v) => `${v}%` } },
                    legend: { show: false },
                    annotations: {
                      yaxis: [{ y: report.sector_average_percentage, borderColor: PRIMARY, strokeDashArray: 4, label: { text: `Avg: ${report.sector_average_percentage.toFixed(1)}%`, style: { color: "#fff", background: PRIMARY, fontSize: "11px", fontWeight: 700 } } }],
                    },
                  }}
                />
              </div>
            </div>

            {/* Charts: Role distribution + Performance distribution */}
            <div className="space-y-6">
              {/* Role distribution donut */}
              {roleDistribution.series.length > 0 && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MdPieChart className="text-xl" style={{ color: SECONDARY }} />
                    <h3 className="font-black text-slate-900 dark:text-white">Role Distribution</h3>
                  </div>
                  <div className="h-52">
                    <Chart
                      type="donut"
                      width="100%"
                      height="100%"
                      series={roleDistribution.series}
                      options={{
                        labels: roleDistribution.labels.map((l) => l.charAt(0).toUpperCase() + l.slice(1)),
                        colors: [PRIMARY, SECONDARY, "#10b981", "#8b5cf6"],
                        legend: { position: "bottom", fontSize: "12px" },
                        dataLabels: { enabled: true },
                        plotOptions: { pie: { donut: { size: "55%", labels: { show: true, total: { show: true, label: "Total", fontSize: "14px", fontWeight: 800 } } } } },
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Performance tier distribution */}
              {performanceDistribution.series.some((v) => v > 0) && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MdTrendingUp className="text-xl" style={{ color: PRIMARY }} />
                    <h3 className="font-black text-slate-900 dark:text-white">Performance Tiers</h3>
                  </div>
                  <div className="space-y-3">
                    {performanceDistribution.labels.map((label, i) => {
                      const count = performanceDistribution.series[i];
                      const total = workers.length;
                      const pct = total > 0 ? (count / total) * 100 : 0;
                      const colors = ["#10b981", "#f59e0b", "#ef4444"];
                      return (
                        <div key={label}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{label}</span>
                            <span className="text-xs font-black text-slate-900 dark:text-white">{count} worker{count !== 1 ? "s" : ""}</span>
                          </div>
                          <div className="h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: colors[i] }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Worker rankings */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MdPeople className="text-xl" style={{ color: PRIMARY }} />
              <h3 className="font-black text-slate-900 dark:text-white text-lg">
                Worker Rankings
              </h3>
              <span className="ml-auto text-sm font-bold text-slate-400">
                {workers.length} worker{workers.length !== 1 ? "s" : ""} evaluated
              </span>
            </div>
            <div className="space-y-3">
              {paginatedWorkers.map((w, idx) => (
                <WorkerCard key={w.evaluatee_id} worker={w} rank={(currentPage - 1) * PAGE_SIZE + idx + 1} />
              ))}
            </div>

            {/* Pagination controls */}
            {sortedWorkers.length > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, sortedWorkers.length)} of {sortedWorkers.length}</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <div className="text-sm text-slate-600">Page {currentPage} / {totalPages}</div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
